import { Outlet, useParams } from "react-router-dom";
import { createContext, useContext, useEffect, useState } from "react";
import { api, getAuth, clearAuth } from "@/lib/api";
import { applyTheme, clearTheme, THEME_PRESETS } from "@/lib/themes";
import { AlertCircle } from "lucide-react";

const TenantCtx = createContext({ tenant: null, plan: null });
export const useTenant = () => useContext(TenantCtx);

const HEAD_IDS = ["las-manifest", "las-theme-color", "las-apple-cap", "las-apple-title", "las-favicon", "las-apple-icon"];
const DEFAULT_TITLE = document.title;

function upsertLink({ rel, href, id }) {
  // Remove any other <link rel="..."> not matching our id, so the per-tenant
  // value is unambiguous.
  document.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => {
    if (el.id !== id) el.remove();
  });
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("link");
    el.id = id;
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertMeta({ name, content, id }) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("meta");
    el.id = id;
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

/**
 * Loads the tenant named in the URL and provides it to every customer-facing
 * page below /t/:slug. Nothing under this route renders until that tenant has
 * loaded, and there is no default tenant or platform page to fall back to: if
 * the tenant cannot be loaded, the visitor sees an error for that tenant.
 */
export default function TenantRoot() {
  const { slug } = useParams();
  const [state, setState] = useState({ tenant: null, plan: null, error: null, loading: true });
  const [attempt, setAttempt] = useState(0);
  const reload = () => setAttempt((n) => n + 1);

  useEffect(() => {
    // Set false when the URL moves to another tenant (or the page is left), so a
    // slow answer for the previous tenant is dropped instead of being shown.
    let current = true;
    setState({ tenant: null, plan: null, error: null, loading: true });
    api.get(`/public/tenant/${slug}`).then((r) => {
      if (!current) return;
      const tenant = r.data.tenant;
      if (tenant.slug !== slug) throw new Error("Tenant mismatch");

      // A customer session kept for this URL must belong to this tenant.
      const scope = `t:${slug}:customer`;
      const session = getAuth(scope);
      if (session && session.user?.tenant_id !== tenant.id) clearAuth(scope);

      const branding = tenant.branding || {};
      const presetId = branding.theme_preset || "warm-coral";
      applyTheme(presetId, branding.primary_color);

      // Per-tenant manifest, favicon, theme colour and title.
      // The manifest path is relative so it is always same-origin.
      const appName = branding.app_name || tenant.business_name || "App";
      const preset = THEME_PRESETS[presetId] || THEME_PRESETS["warm-coral"];
      const iconHref = branding.logo_url || "/icons/default-app-icon-192.png";
      upsertLink({ rel: "manifest", href: `/api/public/tenant/${slug}/manifest.json`, id: "las-manifest" });
      upsertMeta({ name: "theme-color", content: preset.swatch?.[0] || "#EE6A4D", id: "las-theme-color" });
      upsertMeta({ name: "apple-mobile-web-app-capable", content: "yes", id: "las-apple-cap" });
      upsertMeta({ name: "apple-mobile-web-app-title", content: appName, id: "las-apple-title" });
      upsertLink({ rel: "icon", href: iconHref, id: "las-favicon" });
      upsertLink({ rel: "apple-touch-icon", href: iconHref, id: "las-apple-icon" });
      document.title = appName;

      setState({ tenant, plan: r.data.plan, error: null, loading: false });
    }).catch((e) => {
      if (!current) return;
      setState({ tenant: null, plan: null, loading: false, error: e?.response?.data?.detail || "This page could not be loaded" });
    });
    return () => {
      current = false;
      // Leaving this tenant: take its branding off the page.
      HEAD_IDS.forEach((id) => document.getElementById(id)?.remove());
      clearTheme();
      document.title = DEFAULT_TITLE;
    };
  }, [slug, attempt]);

  if (state.loading) {
    return <div className="pwa-shell pwa-shell--plain flex items-center justify-center"><div className="text-sm text-neutral-500">Loading…</div></div>;
  }
  if (state.error) {
    return (
      <div className="pwa-shell pwa-shell--plain flex flex-col items-center justify-center p-8 text-center" data-testid="tenant-error">
        <AlertCircle className="h-12 w-12 text-neutral-400 mb-4" />
        <div className="font-bold text-xl">This page is unavailable</div>
        <p className="text-sm text-neutral-500 mt-2 break-anywhere">{state.error} ({slug})</p>
        <button onClick={reload} className="mt-6 bg-black text-white px-6 py-3 rounded-full text-sm font-semibold min-h-[44px]" data-testid="tenant-error-retry">Try again</button>
      </div>
    );
  }

  return (
    <TenantCtx.Provider value={{ tenant: state.tenant, plan: state.plan, reload }}>
      <Outlet />
    </TenantCtx.Provider>
  );
}
